import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useAuthContext } from "v4/contexts/AuthContext";
import { CORE_GET_TABS_CONFIGURATION } from "v4/data/apiRoutes";
import { STATIC_APP_ROUTES } from "v4/features/front/navigation/data/staticAppRoutes";
import useCurrentRoute from "v4/features/front/navigation/hooks/useCurrentRoute";
import { resolveRouteComponent } from "v4/features/front/navigation/utils/navigation.mapper";
import {
    findNavigationItem,
    getPatternMetadata,
    patternToRegex
} from "v4/features/front/navigation/utils/navigation.utils";
import useFetch from "v4/hooks/useFetch";
import { generateUrl } from "v4/services/api.service";

const NavigationContext = createContext({
    mainNavigation: [],
    detailNavigation: [],
    hiddenMainNavigation: [],
    hiddenDetailNavigation: [],
    isLoading: true,
    isError: false,
    isReady: false,
    getFlattenedNavigation: () => ({ flatMainNavigation: [], flatDetailNavigation: [], flatHiddenMainNavigation: [], flatHiddenDetailNavigation: [] }),
    getRouteConfigByPathname: (path) => null,
    getRouteByNativeIdentifier: (identifier) => null,
    getDefaultProspectTab: () => null,
    isRouteVisibleByNativeIdentifier: (identifier) => false,
    currentRoute: { route: null, routeConfig: null, routeType: null, isDetail: false, isCustomTab: false, params: {} },
});

export function useNavigation() {
    return useContext(NavigationContext);
}

const FALLBACK_URLS = {
    'main_prospects': 'clients-prospects',
    'main_contacts': 'contacts',
    'main_tasks': 'actions',
    'main_quotes': 'devis-offres',
};

export default function NavigationProvider({ children }) {
    const { token } = useAuthContext();
    const [{ data: response, isLoading, isError }, fetchTabsConfiguration] = useFetch();

    useEffect(() => {
        if (!token) return;
        fetchTabsConfiguration({ url: generateUrl(CORE_GET_TABS_CONFIGURATION) });
    }, [fetchTabsConfiguration, token]);

    // --- 1. Préparation de la navigation (Serveur + Statique) ---
    const navigation = useMemo(() => {
        const { main_navigation: main = {}, detail_navigation: detail = {} } = response?.data || {};
        
        const serverMain = main.visible || [];
        const serverDetail = detail.visible || [];
        const serverHiddenMain = main.hidden || [];
        const serverHiddenDetail = detail.hidden || [];

        const allServerItems = [...serverMain, ...serverDetail, ...serverHiddenMain, ...serverHiddenDetail];

        const getUrlByNativeIdentifier = (nativeIdentifier) => {
            const serverItem = findNavigationItem(allServerItems, item => item.native_identifier === nativeIdentifier);
            if (serverItem?.url) return serverItem.url;
            return FALLBACK_URLS[nativeIdentifier] || nativeIdentifier.replace(/^main_/, '').replace(/_/g, '-');
        };

        const resolvedStaticRoutes = STATIC_APP_ROUTES.map(route => ({
            ...route,
            url: route.url.replace(/%(.+?)%/g, (_, id) => getUrlByNativeIdentifier(id))
        }));

        const mainNavigation = [...resolvedStaticRoutes.filter(r => r.native_identifier?.startsWith('main_')), ...serverMain];
        const detailNavigation = [
            ...resolvedStaticRoutes.filter(r => r.native_identifier?.startsWith('detail_') && r.native_identifier !== 'detail_not_found'),
            ...serverDetail
        ];

        // On pré-calcule les listes plates pour accélérer les recherches ultérieures
        const flatten = (items) => items.reduce((acc, item) => {
            acc.push(item);
            if (item.children?.length) acc.push(...flatten(item.children));
            return acc;
        }, []);

        const flatMain = flatten(mainNavigation);
        const flatDetail = flatten(detailNavigation);
        const flatHiddenMain = flatten(serverHiddenMain);
        const flatHiddenDetail = flatten(serverHiddenDetail);
        
        return {
            mainNavigation,
            detailNavigation,
            hiddenMainNavigation: serverHiddenMain,
            hiddenDetailNavigation: serverHiddenDetail,
            allNavigationItems: [...flatMain, ...flatDetail, ...flatHiddenMain, ...flatHiddenDetail],
            flattened: {
                flatMainNavigation: flatMain,
                flatDetailNavigation: flatDetail,
                flatHiddenMainNavigation: flatHiddenMain,
                flatHiddenDetailNavigation: flatHiddenDetail
            },
            isReady: response?.type === 'success:get_tabs_configuration',
            resolvedStaticRoutes,
            getUrlByNativeIdentifier
        };
    }, [response]);

    // --- 2. Moteur de résolution de route par URL ---
    const getRouteConfigByPathname = useCallback((pathname) => {
        if (!pathname) return null;

        const cleanPath = pathname.replace(/^\/+|\/+$/g, '');
        const matches = [];

        const checkMatch = (item, urlPattern, isDetail) => {
            if (!urlPattern || urlPattern === '*') return;
            const regex = patternToRegex(urlPattern, isDetail);

            if (regex.test(cleanPath)) {
                const { score, placeholderCount } = getPatternMetadata(urlPattern);
                matches.push({ item, score, placeholderCount, isDetail });
            }
        };

        const traverse = (items, isDetail) => {
            items.forEach(item => {
                checkMatch(item, item.url, isDetail);
                
                const config = resolveRouteComponent(item, isDetail);
                if (config?.additionalPaths && item.url) {
                    config.additionalPaths.forEach(ap => {
                        const subPath = typeof ap === 'object' ? ap.path : ap;
                        const fullPath = `${item.url.replace(/\/+$/, '')}/${subPath.replace(/^\/+/, '')}`;
                        checkMatch(item, fullPath, isDetail);
                    });
                }

                if (item.children?.length) traverse(item.children, isDetail);
            });
        };

        // On parcourt les listes structurées (car les additionalPaths dépendent de la structure)
        [
            { list: navigation.mainNavigation, isDetail: false },
            { list: navigation.detailNavigation, isDetail: true },
            { list: navigation.hiddenMainNavigation, isDetail: false },
            { list: navigation.hiddenDetailNavigation, isDetail: true }
        ].forEach(({ list, isDetail }) => traverse(list, isDetail));

        // Résolution du meilleur match
        if (matches.length > 0) {
            const best = matches.sort((a, b) => b.score - a.score || a.placeholderCount - b.placeholderCount)[0];
            return { item: best.item, isDetail: best.isDetail };
        }

        // --- ULTIME RECOURS : detail_not_found ---
        const detail404 = navigation.resolvedStaticRoutes.find(r => r.native_identifier === 'detail_not_found');
        if (detail404) {
            const mainProspectsUrl = navigation.getUrlByNativeIdentifier('main_prospects');
            // On vérifie si on est dans le périmètre d'un prospect
            if (patternToRegex(mainProspectsUrl, true).test(cleanPath)) {
                return { item: detail404, isDetail: true };
            }
        }

        // Fallback global (*)
        const catchAll = navigation.allNavigationItems.find(i => i.url === '*');
        return catchAll ? { item: catchAll, isDetail: false } : null;
    }, [navigation]);


    const getRouteByNativeIdentifier = useCallback((nativeIdentifier) => {
        return navigation.allNavigationItems.find(i => i.native_identifier === nativeIdentifier);
    }, [navigation]);

    const getDefaultProspectTab = useCallback(() => {
        // 1. Onglet explicitement marqué par défaut
        const flagged = navigation.flattened.flatDetailNavigation.find(i => i.is_default_tab_for_prospect);
        if (flagged) return flagged;

        // 2. detail_open_tasks s'il est visible
        const visibleOpenTasks = navigation.detailNavigation.find(i => i.native_identifier === 'detail_open_tasks');
        if (visibleOpenTasks) return visibleOpenTasks;

        // 3. Premier onglet visible
        const firstVisible = navigation.detailNavigation.find(i => i.label);
        if (firstVisible) return firstVisible;

        // 4. detail_open_tasks en dernier recours (même masqué)
        return getRouteByNativeIdentifier('detail_open_tasks');
    }, [navigation, getRouteByNativeIdentifier]);

    const isRouteVisibleByNativeIdentifier = useCallback((nativeIdentifier) => {
        return [...navigation.mainNavigation, ...navigation.detailNavigation].some(i => i.native_identifier === nativeIdentifier);
    }, [navigation]);

    const getFlattenedNavigation = useCallback(() => navigation.flattened, [navigation]);

    const currentRoute = useCurrentRoute({
        ...navigation,
        getRouteConfigByPathname,
        getRouteByNativeIdentifier
    });

    return (
        <NavigationContext.Provider value={{
            ...navigation,
            isLoading,
            isError,
            getFlattenedNavigation,
            getRouteConfigByPathname,
            getRouteByNativeIdentifier,
            getDefaultProspectTab,
            isRouteVisibleByNativeIdentifier,
            currentRoute
        }}>
            {children}
        </NavigationContext.Provider>
    );
}
